import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, ArrowRight, CheckCircle2, Clock, PlayCircle, Users2 } from "lucide-react";

import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ModuleTitleTRAINING } from "@/components/training/ModuleTitleTRAINING";

const STATUS_LABEL = {
  not_started: "Not started",
  in_progress: "In progress",
  completed: "Completed",
};

const STATUS_PILL = {
  not_started: "bg-sage-300/40 text-forest-800 border-sage-300",
  in_progress: "bg-terracotta-500/15 text-terracotta-600 border-terracotta-500/30",
  completed: "bg-forest-600 text-cream-100 border-forest-800",
};

const ROLE_LEVEL = { volunteer: 0, employee: 1, manager: 2, po_admin: 3, super_admin: 4 };

export default function TrainingLanding() {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);

  const level = ROLE_LEVEL[user?.role || "employee"] ?? 1;
  const isManager = level >= 2;
  const isPO = level >= 3;

  const load = async () => {
    try {
      const { data } = await api.get("/training/mine");
      setAssignments(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []);

  const overdueCount = useMemo(
    () => assignments.filter((a) => a.overdue).length,
    [assignments],
  );
  const requiredIncomplete = useMemo(
    () => assignments.filter((a) => a.required && a.status !== "completed").length,
    [assignments],
  );

  return (
    <div className="space-y-8" data-testid="training-landing">
      <section>
        <ModuleTitleTRAINING />
        <h1 className="mt-4 font-serif text-forest-900 text-4xl sm:text-5xl">
          Learning &amp; Formation, For The Whole Team.
        </h1>
        <p className="mt-3 text-charcoal-soft max-w-2xl leading-relaxed">
          Every training you&apos;re assigned lives here — short videos, quick quizzes, and a
          simple attestation when you&apos;re done. Take a breath, then step in.
        </p>
      </section>

      {overdueCount > 0 && (
        <div
          className="patria-card p-4 border-l-4 border-terracotta-500 flex items-start gap-3 bg-terracotta-500/5"
          data-testid="training-overdue-banner"
        >
          <AlertCircle className="h-5 w-5 text-terracotta-500 mt-0.5" strokeWidth={1.5} />
          <div>
            <p className="font-serif text-forest-900 text-lg">
              {overdueCount} required training{overdueCount === 1 ? "" : "s"} past due
            </p>
            <p className="text-sm text-charcoal-soft mt-1">
              Please complete these soon — People Operations will follow up if they stay open.
            </p>
          </div>
        </div>
      )}

      <section>
        <div className="flex items-baseline justify-between">
          <h3 className="font-serif text-forest-900 text-2xl">My Assigned Training</h3>
          <p className="text-xs text-charcoal-muted">
            {requiredIncomplete} required incomplete
          </p>
        </div>
        {assignments.length === 0 && (
          <div className="patria-card p-6 mt-4">
            <p className="text-sm text-charcoal-muted italic">
              You have no training assigned right now. Enjoy the quiet.
            </p>
          </div>
        )}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {assignments.map((a) => (
            <Link
              key={a.id}
              to={`/training/${a.id}`}
              className="patria-card patria-card-hover p-5 flex flex-col gap-3"
              data-testid={`training-card-${a.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] ${STATUS_PILL[a.status]}`}
                  data-testid={`training-status-${a.id}`}
                >
                  {STATUS_LABEL[a.status]}
                </span>
                {a.required ? (
                  <span className="inline-flex items-center rounded-full border border-terracotta-500/30 bg-terracotta-500/15 text-terracotta-600 px-2 py-0.5 text-[11px]">
                    Required
                  </span>
                ) : (
                  <span className="text-[11px] text-charcoal-muted">Optional</span>
                )}
              </div>
              <p className="font-serif text-forest-900 text-xl leading-snug">
                {a.training_title}
              </p>
              <div className="flex items-center gap-3 text-xs text-charcoal-muted">
                {a.due_date && (
                  <span
                    className={`inline-flex items-center gap-1 ${
                      a.overdue ? "text-terracotta-600 font-medium" : ""
                    }`}
                  >
                    <Clock className="h-3.5 w-3.5" strokeWidth={1.5} />
                    {a.overdue ? "OVERDUE — " : "Due "}
                    {(a.due_date || "").split("T")[0]}
                  </span>
                )}
                {a.status === "completed" && a.completed_at && (
                  <span className="inline-flex items-center gap-1">
                    <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    {(a.completed_at || "").split("T")[0]}
                  </span>
                )}
              </div>
              <span className="mt-auto inline-flex items-center text-forest-600 font-medium text-sm">
                {a.status === "completed" ? "Review" : a.status === "in_progress" ? "Continue" : "Start"}
                <ArrowRight className="ml-1 h-4 w-4" strokeWidth={1.5} />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {(isManager || isPO) && (
        <section className="patria-card p-6">
          <div className="flex items-center gap-2">
            <Users2 className="h-4 w-4 text-forest-600" strokeWidth={1.5} />
            <h3 className="font-serif text-forest-900 text-xl">
              {isPO ? "People Operations tools" : "Manager tools"}
            </h3>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Link
              to="/training/team"
              className="patria-card patria-card-hover p-4"
              data-testid="training-team-link"
            >
              <p className="text-xs uppercase tracking-widest text-charcoal-muted">
                Compliance
              </p>
              <p className="mt-1 font-medium text-forest-900">
                {isPO ? "All teams" : "My direct reports"}
              </p>
            </Link>
            {isPO && (
              <>
                <Link
                  to="/training/admin"
                  className="patria-card patria-card-hover p-4"
                  data-testid="training-admin-link"
                >
                  <p className="text-xs uppercase tracking-widest text-charcoal-muted">Content</p>
                  <p className="mt-1 font-medium text-forest-900">Manage Trainings</p>
                </Link>
                <Link
                  to="/training/reports"
                  className="patria-card patria-card-hover p-4"
                  data-testid="training-reports-link"
                >
                  <p className="text-xs uppercase tracking-widest text-charcoal-muted">Reporting</p>
                  <p className="mt-1 font-medium text-forest-900">Compliance Dashboard</p>
                </Link>
              </>
            )}
          </div>
        </section>
      )}

      <div className="flex items-center gap-2 text-xs text-charcoal-muted italic">
        <PlayCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
        Videos may be direct uploads or embedded from YouTube/Vimeo.
      </div>
    </div>
  );
}
